import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuthContext } from "@src/context/authContext";
import AdminHooks from "@src/context/hooks/adminHooks";
import { CardContainer } from "@src/components/cards";
import { DEFAULT_LANGUAGE } from "@src/constants/constants";
import {
  TabSelector,
  Button,
  Divider,
  FormInput as Input,
  Loader,
  Modal,
  SearchInput,
} from "@src/components/atoms";
import MultiLevelMenu from "./MultiLevelMenu";
import SearchDynamicContent from "./SearchDynamicContent"; //switch to newer version
import { useToastContext } from "@src/context/toastContext";
import { IndustryContentEditor } from "./IndustryContentEditor";

const DynamicContentPage = () => {
  const auth = useAuthContext();
  const { showToast } = useToastContext();

  const {
    data: allDynamicContent,
    //industries,
    allIndustries,
    languages,
    isPending,
    isSettled,
    //error: errorAll,
  } = AdminHooks.useGetAllDynamicContent(auth.activeRole);

  const saveMutation = AdminHooks.useSaveDynamicContent(() =>
    handleCleanAfterMutation(),
  );
  const deleteMutation = AdminHooks.useDeleteDynamicContent(() =>
    handleCleanAfterMutation(),
  );

  // Component state
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedKeyContent, setSelectedKeyContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [changed, setChanged] = useState([]);
  const [isNewKey, setIsNewKey] = useState(null);
  const [isConfirmingSave, setIsConfirmingSave] = useState(false);

  const [showCreateFormForColumn, setShowCreateFormForColumn] = useState(-1);
  const [newDynamicContentKey, setNewDynamicContentKey] = useState("");

  //search handler
  const onSearchSelect = (e) => {
    setKeyAndContent(e);
    setSelectedColumns(e.split("__"));
    setIsEditing(true);
  };

  //prepare keys for multilevel columns
  const allDynamicContentKeys = useMemo(() => {
    const combined = [
      ...new Set(
        allIndustries.flatMap((ind) =>
          Object.keys(allDynamicContent[ind] || {}),
        ),
      ),
    ];

    return combined;
  }, [allDynamicContent, allIndustries]);

  //Checking hash on page load - after the keys have been populated
  const hasProcessedHash = useRef(false);
  useEffect(() => {
    if (
      hasProcessedHash.current ||
      !isSettled ||
      allDynamicContentKeys.length === 0
    )
      return;

    const cleanHash = window.location.hash?.startsWith("#")
      ? window.location.hash.substring(1)
      : window.location.hash;

    const values = cleanHash.split("__");

    if (values.length) {
      selectColumns(values);
      setShowCreateFormForColumn(-1);
    }

    if (allDynamicContentKeys.includes(cleanHash)) {
      setKeyAndContent(cleanHash);
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }

    hasProcessedHash.current = true;
  }, [allDynamicContentKeys, isSettled]);

  //updating hash on user selection
  const onHashChange = (hash) => {
    if (!hash) hash = window.location.hash;
    if (hash) {
      const cleanHash = hash.startsWith("#") ? hash.substring(1) : hash;
      const values = cleanHash.split("__");
      if (values.length) {
        selectColumns(values);
        setShowCreateFormForColumn(-1);
      }
    }
  };
  const updateHash = () => {
    const hashValue = getPath();
    window.history.pushState(
      null,
      null,
      hashValue.length ? `#${hashValue}` : window.location.pathname,
    );
  };

  useEffect(() => {
    const handleHashChange = () => onHashChange();
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast(`Copied text:\n"${text}"`, "success");
  };

  const isLoadingState = () => {
    return isLoading || isPending;
  };

  //Columns builder and selection management for MultiLevel menu
  const getColumns = () => {
    const recursiveConversion = (map, key, idx, splittedValue) => {
      const value = splittedValue[idx];
      if (idx === splittedValue.length - 1) {
        map[value] = key;
      } else {
        map[value] = map[value] || {};
        map[value] = recursiveConversion(
          map[value],
          key,
          idx + 1,
          splittedValue,
        );
      }
      return map;
    };

    return allDynamicContentKeys.reduce(
      (map, key) => recursiveConversion(map, key, 0, key.split("__")),
      {},
    );
  };

  //set data when selection reached a valid key
  const setKeyAndContent = (keyString) => {
    const contentForKey = () => {
      const result = {};
      allIndustries.forEach((industry) => {
        const v = allDynamicContent?.[industry]?.[keyString];
        const formatted = Array.isArray(v)
          ? v.map((i) => i?.content_value)
          : typeof v === "string"
            ? [v]
            : [];
        result[industry] = formatted;
      });
      return result;
    };
    const v = contentForKey();
    setSelectedKeyContent({ ...v });
    setSelectedKey(keyString);
  };

  const selectColumns = (selectedColumns) => {
    setSelectedColumns(selectedColumns);
    const keyString = selectedColumns.join("__");
    const isReplacement = allDynamicContentKeys.includes(keyString);
    if (!!isReplacement) {
      setKeyAndContent(keyString);
      setIsEditing(isReplacement);
    }
  };

  useEffect(() => {
    if (selectedColumns.length > 0) {
      updateHash();
    }
  }, [selectedColumns]);

  const onSelect = (column, value) => {
    if (isNewKey) {
      alert(
        "You must save your new key first before you can browse other keys",
      );
    } else {
      let newSelected = [...selectedColumns];
      newSelected.splice(column);
      newSelected.push(value);
      selectColumns(newSelected);
      setShowCreateFormForColumn(-1);
    }
  };

  const getPath = () => selectedColumns.join("__");

  //to add a new DC key
  const showAddPopUp = (columnIdx) => {
    selectColumns([...selectedColumns].slice(0, columnIdx));
    setIsEditing(false);
    setShowCreateFormForColumn(columnIdx);
  };
  const handleAddNewToColumn = (e, columnIdx, newValue) => {
    e.preventDefault();
    const newColumns = [...selectedColumns];
    newColumns.splice(columnIdx);
    newColumns.push(newValue);

    const keyString = newColumns.join("__");

    // If the key doesn't already exist, treat it as a new one
    const isAlreadyPresent = allDynamicContentKeys.includes(keyString);
    const treatAsNew = !isAlreadyPresent;

    setSelectedColumns(newColumns);
    setShowCreateFormForColumn(-1);
    treatAsNew && setIsNewKey(keyString);
    setKeyAndContent(keyString, true);
    setIsEditing(true);
  };

  const isAddNewKeyValid = () => {
    if (newDynamicContentKey.length == 0) {
      return false;
    }

    const path = getPath();
    const newKey = `${path}${path !== "" ? "__" : ""}${newDynamicContentKey}`;

    return !allDynamicContentKeys.some((key) => {
      const keyParts = key.split("__");
      return (
        key === newKey ||
        newKey.startsWith(key + "__") ||
        (keyParts.length > 1 && newKey === keyParts.slice(0, -1).join("__"))
      );
    });
  };

  //to cancel the current edit
  const handleCancel = (resetNav = true) => {
    setChanged((prev) => prev.filter((p) => p.name !== selectedKey));
    setIsEditing(false);
    setSelectedKey(null);
    setSelectedKeyContent(null);
    setIsNewKey(null);
    if (resetNav) {
      selectColumns(selectedColumns.slice(0, -1));
    }
  };

  //to clear all the edits
  const handleClear = (resetNav = true) => {
    setChanged([]);
    setIsNewKey(null);
    if (resetNav) {
      selectColumns([]);
    }
  };

  //to clear screen after mutation
  const handleCleanAfterMutation = () => {
    setChanged([]);
    setIsNewKey(null);
    setIsEditing(false);
    selectedKey && setSelectedKey(null);
    selectedKeyContent && setSelectedKeyContent(null);
    setNewDynamicContentKey("");
    selectColumns(selectedColumns.slice(0, -1));
  };

  //to delete an existing key
  const handleRemoveEntireKey = async () => {
    const key = getPath();
    let confirmation = prompt(
      `To delete this entire Dynamic Content key, please confirm by re-entering its key name:\n\n${key}`,
    );

    if (confirmation?.trim().toLowerCase() !== key) {
      showToast("Cancelled, nothing was deleted", "warning");
      return;
    }

    const itemsToRemove = [];

    // Delete from each industry if there are strings
    allIndustries?.forEach((industry) => {
      if (allDynamicContent[industry][key]?.length) {
        const item = {
          industry: industry,
          lang: selectedLanguage,
          name: key,
        };
        itemsToRemove.push(item);
      }
    });

    try {
      await Promise.all(
        itemsToRemove.map(async (item) => {
          await deleteMutation.mutateAsync({
            ...item,
          });
        }),
      );

      showToast(`Deleted the dynamic content successfully`, "success");
    } catch (error) {
      console.error("Error saving dynamic content:", error);
      showToast("Error deleting some items. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  //to save edits
  const confirmSave = () => {
    if (changed.length === 0) return;
    setIsConfirmingSave(true);
  };

  const handleSave = async () => {
    setIsConfirmingSave(false);
    const numChanges = changed.length;
    if (!numChanges) return;

    setIsLoading(true);
    try {
      await Promise.all(
        changed.map(async (item) => {
          const filteredContent = item.content.filter(
            (text) => text.length > 0,
          );

          await saveMutation.mutateAsync({
            ...item,
            content: filteredContent,
          });
        }),
      );

      showToast(`Saved ${numChanges} Dynamic Content changes`, "success");
    } catch (error) {
      console.error("Error saving dynamic content:", error);
      showToast("Error saving some items. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (import.meta.env.VITE_REACT_APP_ENV === "demo") {
    return (
      <CardContainer>
        The Dynamic Content editor does not work in Demo mode.
        <br />
        Please make your changes on the{" "}
        <a href="https://qa.signol.app/dashboard/dynamiccontent">QA Admin</a>,
        then (if needed) schedule a time to release the QA Dynamic Content to
        Production.
      </CardContainer>
    );
  }

  if (isLoadingState()) {
    return (
      <CardContainer padding={5}>
        <Loader
          size="2xl"
          text="Loading dynamic content..."
          textColor="primary"
        />
      </CardContainer>
    );
  }

  //Buttons around editable section
  const elemsSaveCancel = (
    <div className="flex flex-col">
      <div className="flex flex-row justify-end">
        <span title="Abandons all pending changes on this key and exits the editing view">
          <Button
            color="neutral"
            onClick={() => {
              handleCancel(true);
            }}
            icon="arrowBack"
          >
            Cancel
          </Button>
        </span>
        <span title="Remove this string from all industries, and Global">
          <Button
            color="red"
            onClick={
              isNewKey
                ? () => {
                    handleCancel(true);
                  }
                : handleRemoveEntireKey
            }
            icon="close"
          >
            Remove
          </Button>
        </span>
        <span title="Change count is per industry per key (not per-textbox) and does not detect Undos">
          <Button
            onClick={confirmSave}
            disabled={changed.length === 0 || saveMutation.isPending}
            icon="save"
          >
            Save {changed.length} {changed.length === 1 ? "change" : "changes"}
          </Button>
        </span>
      </div>
    </div>
  );

  //change tracking during editing
  const updateChangedContent = (industry, newContent) => {
    setChanged((prev) => {
      const others = prev.filter(
        (entry) => !(entry.industry === industry && entry.name === selectedKey),
      );
      return [
        ...others,
        {
          industry,
          lang: selectedLanguage,
          name: selectedKey,
          content: newContent,
        },
      ];
    });
  };

  const getEditableStrings = (industry) => {
    const changedEntry = changed.find(
      (entry) =>
        entry.industry === industry &&
        entry.name === selectedKey &&
        entry.lang === selectedLanguage,
    );

    return changedEntry?.content || selectedKeyContent?.[industry] || [];
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <h2 className="text-center">Dynamic Content</h2>
      {allDynamicContentKeys && (
        <>
          <SearchDynamicContent
            keys={allDynamicContentKeys}
            onSelect={onSearchSelect}
            allIndustriesData={allDynamicContent}
          />
        </>
      )}

      <div className="grow">
        <CardContainer shadow={false}>
          {!!languages && languages.length > 1 && (
            <TabSelector
              options={languages}
              selected={selectedLanguage}
              onClick={(value) => {
                setSelectedLanguage(value);
              }}
            />
          )}
          <MultiLevelMenu
            selected={selectedColumns}
            options={getColumns()}
            onSelect={onSelect}
            onAddToColumn={(columnIdx) => showAddPopUp(columnIdx)}
            showSearch={false}
          />
        </CardContainer>
      </div>

      <CardContainer shadow={false}>
        {showCreateFormForColumn >= 0 && (
          <form
            className="bg-white border-2 m-4 rounded-md"
            onSubmit={(e) =>
              handleAddNewToColumn(
                e,
                showCreateFormForColumn,
                newDynamicContentKey,
              )
            }
          >
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-bold text-primary-500">
                Add a new replacement
              </h3>
              <div className="mt-2 leading-5">
                <p>You're about to add a new replacement to:</p>
                <ul className="m-3">
                  <li>
                    Language:
                    <span className="pl-2 font-bold">{selectedLanguage}</span>
                  </li>
                  <li>
                    Path:
                    <span className="pl-2 font-bold">
                      {getPath() === "" ? "<Root Level>" : getPath()}
                    </span>
                  </li>
                </ul>
                <div>
                  <label className="text-primary-500">
                    <strong>New Key Identifier:</strong>
                    <div className="flex items-center">
                      <code className="align-middle">
                        {getPath() === "" ? "" : getPath() + "__"}
                      </code>
                      {/*TODO - replace by proper search input component */}
                      <input
                        name="newReplacement"
                        className={"border-1 p-2 rounded-2xl grow"}
                        placeholder="something__something__dark_side"
                        value={newDynamicContentKey}
                        onChange={(e) => {
                          const corrected = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9_][^a-z0-9_]+/g, "_")
                            .replace(/\-/g, "_")
                            .replace(/[\\\/]/g, "__")
                            .replace(/[^a-z0-9_]/g, "")
                            .replace(/___+/g, "__")
                            .replace(/^_+/, "");
                          e.target.value = corrected;
                          setNewDynamicContentKey(e.target.value);
                        }}
                      />
                    </div>
                  </label>
                </div>

                <ul className="text-gray-400 text-sm italic">
                  <li>
                    ℹ️ To add sub-levels, use two underscores, eg.:
                    <code className="mx-1">'level__sublevel__my_value'</code>
                  </li>
                  <li>
                    ℹ️ Don't create folders/directories, just create the end
                    item, eg:{" "}
                    <code className="font-semibold mx-1">
                      customers__sg__contact_name
                    </code>
                    , not
                    <code className="font-semibold mx-1">customers__sg</code>
                  </li>
                </ul>
                <Button submit disabled={!isAddNewKeyValid()}>
                  Create
                </Button>
                {newDynamicContentKey && !isAddNewKeyValid() && (
                  <em>
                    ⚠️ '{newDynamicContentKey}' is invalid. It either already
                    exists, or is trying to "sub-level" something that already
                    exists
                  </em>
                )}
              </div>
            </div>
          </form>
        )}
        {Object.keys(allDynamicContent).length === 0 && <div>No data :(</div>}
        {!isEditing && showCreateFormForColumn < 0 && (
          <div className="w-full  text-gray-400 rounded-md p-4">
            <p>Continue browsing, the editor will appear here ☺</p>
            <p>Incomplete path: {selectedColumns.join("__")}</p>
          </div>
        )}

        {isEditing && (
          <section id="editor" className="w-full">
            <div className="border-2 rounded-md p-4 m-4">
              <header className="text-lg">
                <label className="font-bold text-primary-500">
                  Content Key:
                  <pre className="inline-block mx-4">{selectedKey}</pre>
                </label>
                <button
                  type="button"
                  title="Copy Key"
                  className="cursor-pointer transform focus:translate-y-px focus:outline-none"
                  onClick={() => copyToClipboard(selectedKey)}
                >
                  📋
                </button>
              </header>
              {isNewKey === selectedKey && (
                <div className="border-2 border-orange-500 rounded-md p-2 m-2 bg-orange-100">
                  This new key is not saved yet. You need to edit the fields
                  below and press save to complete the process.
                </div>
              )}
              {elemsSaveCancel}

              {allIndustries.map((industry) => (
                <IndustryContentEditor
                  key={industry}
                  industry={industry}
                  industryStrings={getEditableStrings(industry)}
                  onChange={(newContent) =>
                    updateChangedContent(industry, newContent)
                  }
                />
              ))}
              {elemsSaveCancel}
            </div>
          </section>
        )}
        {changed.length > 0 && (
          <div className="flex flex-row justify-end">
            <span title="Abandons all pending changes and reloads from the server">
              <Button
                color="gray"
                disabled={changed.length === 0}
                onClick={() => handleClear(false)}
                icon="refresh"
              >
                Clear all changes ({changed.length})
              </Button>
            </span>
          </div>
        )}
      </CardContainer>
      {!!isConfirmingSave && (
        <Modal
          size="fullwidth"
          open={!!isConfirmingSave}
          handleClose={() => setIsConfirmingSave(false)}
        >
          <div className="flex flex-col gap-2 break-words whitespace-normal p-3">
            <p className="font-semibold">{`CONFIRMATION: Save all ${changed.length} changes?`}</p>
            <p>{`Your changes are: (Undos included)`}</p>

            <ol className="list-decimal pl-3">
              {changed.map((c, i) => (
                <li key={i}>
                  {c.name.split("__").map((segment, idx, arr) => (
                    <code key={idx} className="whitespace-pre-line">
                      {segment}
                      {idx < arr.length - 1 && "__"}
                      <wbr />
                    </code>
                  ))}
                  {" → "}
                  {c.industry}
                </li>
              ))}
            </ol>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DynamicContentPage;
